import { fetchAuthSession } from 'aws-amplify/auth'
import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from 'axios'

import { handleException } from 'clients/exception-handling'
import type {
  ApiClient,
  SessionManager,
  TokenManager,
} from 'clients/yogi-api/types'

/**
 * Adapter: concrete HTTP client backed by axios.
 *
 * Implements all three ports (ApiClient, TokenManager, SessionManager)
 * and wires up request/response interceptors for automatic token
 * refresh and session-expiry detection.
 */
export class YogiAxiosClient
  implements ApiClient, TokenManager, SessionManager
{
  private axiosInstance: AxiosInstance

  // Session-expiry state
  private _sessionExpiredFired = false
  private _onSessionExpired: (() => void) | null = null

  constructor(baseURL?: string) {
    this.axiosInstance = axios.create({
      baseURL: baseURL ?? import.meta.env.VITE_YOGI_API_BASE_URL,
      timeout: 180000,
    })

    this.setupInterceptors()

    // Dev-only console trigger: window.__forceSessionExpiry()
    if (import.meta.env.DEV && typeof window !== 'undefined') {
      ;(
        window as Window &
          typeof globalThis & { __forceSessionExpiry: () => void }
      ).__forceSessionExpiry = () => this.handleExpiry()
    }
  }

  // ---------------------------------------------------------------------------
  // ApiClient
  // ---------------------------------------------------------------------------

  async get<T = unknown>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.get<T>(url, config)
  }

  async post<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.post<T>(url, data, config)
  }

  async put<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.put<T>(url, data, config)
  }

  async delete<T = unknown>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.delete<T>(url, config)
  }

  async patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.patch<T>(url, data, config)
  }

  async options<T = unknown>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.options<T>(url, config)
  }

  // ---------------------------------------------------------------------------
  // TokenManager
  // ---------------------------------------------------------------------------

  setToken(token: string): void {
    this.axiosInstance.defaults.headers.common['Authorization'] = token
  }

  clearToken(): void {
    delete this.axiosInstance.defaults.headers.common['Authorization']
  }

  setSharedToken(token: string): void {
    this.axiosInstance.defaults.headers.common['project-token'] = token
  }

  hasSharedToken(): boolean {
    return !!this.axiosInstance.defaults.headers.common['project-token']
  }

  // ---------------------------------------------------------------------------
  // SessionManager
  // ---------------------------------------------------------------------------

  registerExpiredHandler(handler: () => void): void {
    this._onSessionExpired = handler
    this._sessionExpiredFired = false
  }

  unregisterExpiredHandler(): void {
    this._onSessionExpired = null
  }

  resetExpired(): void {
    this._sessionExpiredFired = false
  }

  markExpired(): void {
    this._sessionExpiredFired = true
  }

  get isExpired(): boolean {
    return this._sessionExpiredFired
  }

  // ---------------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------------

  private handleExpiry(): void {
    if (this._sessionExpiredFired) return
    this._sessionExpiredFired = true

    if (this._onSessionExpired) {
      this._onSessionExpired()
    } else if (typeof window !== 'undefined') {
      // Fallback: no React handler registered yet — redirect directly.
      const currentPath = window.location.pathname + window.location.search
      const redirect =
        currentPath && currentPath !== '/' && currentPath !== '/sign-in'
          ? `?redirect=${encodeURIComponent(currentPath)}`
          : ''
      window.location.href = `/sign-in${redirect}`
    }
  }

  private setupInterceptors(): void {
    // -- Request: attach token, block if session expired --
    this.axiosInstance.interceptors.request.use(async (config) => {
      if (this._sessionExpiredFired) {
        const controller = new AbortController()
        controller.abort()
        config.signal = controller.signal
        return config
      }

      const headers = config.headers ?? {}
      const hasAuthHeader =
        Boolean((headers as Record<string, string>)['Authorization']) ||
        Boolean(this.axiosInstance.defaults.headers.common['Authorization'])

      if (!hasAuthHeader) {
        try {
          const { tokens } = await fetchAuthSession()
          const token = tokens?.idToken?.toString()
          if (token) {
            this.setToken(token)
            ;(headers as Record<string, string>)['Authorization'] = token
            config.headers = headers
          }
        } catch (error) {
          handleException(error, {
            operation: 'api.session.read',
            disposition: 'delegated',
            owner: 'api.response.session-refresh',
          })
        }
      }

      return config
    })

    // -- Response: refresh on 401, trigger session expiry on failure --
    // An anonymous share-link viewer (a shared token and no Authorization on
    // the client defaults, which `setToken` writes on sign-in) has no session
    // to refresh or expire: its 401 (a route that needs a signed-in user)
    // rejects as is, so the page's other requests still run. A signed-in user
    // keeps refresh and expiry even after opening a share link, because the
    // shared token is never cleared.
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config
        const signedIn = Boolean(
          this.axiosInstance.defaults.headers.common['Authorization'],
        )

        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          (signedIn || !this.hasSharedToken())
        ) {
          originalRequest._retry = true

          try {
            const { tokens } = await fetchAuthSession()
            const token = tokens?.idToken?.toString()

            if (!token) {
              throw new Error('No valid token found')
            }

            this.setToken(token)
            originalRequest.headers['Authorization'] = token

            return this.axiosInstance(originalRequest)
          } catch (e) {
            handleException(e, {
              operation: 'api.session.refresh',
              disposition: 'delegated',
              owner: 'request-operation-boundary',
            })
            this.handleExpiry()
            return Promise.reject(e)
          }
        }

        return Promise.reject(error)
      },
    )
  }
}
